import { clearPassportEntity, seedPassportEntity } from '@frontend-platform/authz';
import { getSharedState, subscribeKey, subscribeSharedState } from '@frontend-platform/host-state';
import { setEmbedPortalContainer } from '@frontend-platform/ui-lib';
import type { RouterHistory } from '@tanstack/react-router';
import { createElement, type ReactNode, StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { markFederationMount } from './federation-context';
import {
  createShadowMountContainer,
  ensureDocumentFonts,
  ensureDocumentPropertyRules,
  loadCSSFromLink,
} from './federation-mount';
import {
  createFederationRouterSync,
  type FederationRoutingProps,
  type SyncableRouter,
} from './federation-router-sync';

/**
 * Federation whole-app mount lifecycle. Consumed by apps/observation-tools —
 * the only federated remote today — via `@frontend-platform/federation-runtime`,
 * and deliberately NOT a federation shared module. Owns the mount/unmount
 * choreography every remote must repeat identically: federation flag, document
 * fonts, passport seeding from @frontend-platform/host-state, shadow container + portal
 * target, host↔remote router sync, React root, and the mirrored teardown.
 */

function syncPassport(): void {
  const s = getSharedState();
  const passportUser = s?.passportUser;
  // clearSharedState() on host logout republishes with passportUser absent —
  // the seeded entity must be torn down here too, or a permission check in
  // this remote keeps passing for a user who is no longer logged in on the
  // host. This previously just returned, leaving the stale entity seeded.
  if (!passportUser || Object.keys(passportUser).length === 0) {
    clearPassportEntity();
    return;
  }
  const bot = s?.bots?.currentBot;
  const orgId = typeof bot?.orgId === 'number' ? bot.orgId : undefined;
  const botId = typeof bot?.id === 'string' ? bot.id : undefined;
  seedPassportEntity(passportUser, { orgId, botId });
}

function hostStateLogDetails(state: ReturnType<typeof getSharedState>) {
  return {
    hasState: Boolean(state),
    userId: state?.user?.id,
    botId: state?.bots?.currentBot?.id,
    botRefId: state?.botRefId,
    supportDesk: state?.bots?.currentSupportDesk,
    hasPassportUser: Boolean(state?.passportUser),
    environmentCount: state?.environments?.length ?? 0,
    // Full array, not just the count — each entry carries its own botRefId
    // (Staging/SANDBOX vs Production/LIVE for a non-EP bot), which the
    // top-level `botRefId` above does not: that field is only the
    // *currently selected* environment (Vuex `scaffold/currentBotRefId`),
    // not a per-environment map.
    environments: state?.environments,
  };
}

export interface FederationAppConfig<
  TRouter extends SyncableRouter,
  TMountProps extends FederationRoutingProps,
> {
  /** Document-level typekit link id, e.g. 'test-suite-remote-fonts'. */
  fontsLinkId: string;
  /** Resolved in the app module so the URL is relative to the remote's own chunk: `new URL('../style.css', import.meta.url).href`. */
  styleHref: string;
  containerTagName?: string;
  createRouter: (history: RouterHistory) => TRouter;
  /** Maps the host base to the prefix the app's route tree expects; omit when route paths are already host-relative. */
  getInternalBaseURL?: (basePath: string | undefined) => string | undefined;
  /** Receives the mount props minus the routing keys. Optional when the host seeds auth via the shared singleton bridge. */
  seedAuth?: (props: Omit<TMountProps, keyof FederationRoutingProps>) => void;
  clearAuth?: () => void;
  onBeforeRender?: () => void;
  render: (router: TRouter) => ReactNode;
}

export function createFederationApp<
  TRouter extends SyncableRouter,
  TMountProps extends FederationRoutingProps,
>(config: FederationAppConfig<TRouter, TMountProps>) {
  /**
   * Everything one `mount()` call owns and its matching `unmount()` must tear
   * down — held per mount rather than in the factory closure.
   *
   * A single shared set of these fields is unsafe because mounts can overlap:
   * `mount()` awaits the shadow-root stylesheet, so a user who leaves
   * Calibration and returns before that settles starts mount B while mount A
   * is still in flight. With shared fields, B overwrites A's, and A's late
   * `unmount(itsOwnElement)` would then tear down *B's* live root and
   * container. Keying on the element each mount was given keeps that
   * impossible: a stale unmount finds its own (already-superseded) record, or
   * none at all.
   */
  interface MountRecord {
    root: Root | null;
    container: HTMLElement | null;
    disposeRouterSync: (() => void) | null;
    disposePassportSync: (() => void) | null;
    disposeHostStateLogging: (() => void) | null;
  }

  const mounts = new Map<HTMLElement, MountRecord>();
  // Identifies the newest mount, so a stale one can tell it no longer owns the
  // document-level state (portal container, federation flag) on teardown.
  let currentEl: HTMLElement | null = null;

  function disposeRecord(record: MountRecord): void {
    record.disposePassportSync?.();
    record.disposePassportSync = null;
    record.disposeHostStateLogging?.();
    record.disposeHostStateLogging = null;
    record.disposeRouterSync?.();
    record.disposeRouterSync = null;
  }

  async function mount(el: HTMLElement, props?: TMountProps) {
    const { basePath, initialPath, onNavigate, ...authProps } = (props ?? {}) as TMountProps;

    // Remounting into the same element (a fast leave/return) must not orphan
    // the previous record's subscriptions.
    const previous = mounts.get(el);
    if (previous) disposeRecord(previous);

    const record: MountRecord = {
      root: null,
      container: null,
      disposeRouterSync: null,
      disposePassportSync: null,
      disposeHostStateLogging: null,
    };
    mounts.set(el, record);
    currentEl = el;

    markFederationMount(true);
    console.info('[federation] mounting remote', {
      containerTagName: config.containerTagName,
      basePath,
      initialPath,
      hostState: hostStateLogDetails(getSharedState()),
    });
    ensureDocumentFonts(config.fontsLinkId);
    ensureDocumentPropertyRules(config.styleHref);
    config.clearAuth?.();
    config.seedAuth?.(authProps);

    syncPassport();
    const unsub1 = subscribeKey('passportUser', syncPassport);
    const unsub2 = subscribeKey('bots', syncPassport);
    record.disposePassportSync = () => {
      unsub1();
      unsub2();
    };
    record.disposeHostStateLogging = subscribeSharedState((state) => {
      console.info('[federation] host-state published to remote', hostStateLogDetails(state));
    });

    const { router, onParentNavigate, dispose } = createFederationRouterSync({
      routing: { basePath, initialPath, onNavigate },
      internalBase: config.getInternalBaseURL?.(basePath),
      createRouter: config.createRouter,
    });
    record.disposeRouterSync = dispose;

    config.onBeforeRender?.();
    const container = createShadowMountContainer(el, {
      styleHref: config.styleHref,
      containerTagName: config.containerTagName,
    });
    setEmbedPortalContainer(container);
    record.container = container;

    // Render only after the shadow-root stylesheet has loaded; the host keeps
    // its loader up until mount() resolves, so no unstyled frame is visible.
    await loadCSSFromLink(el);

    // unmount() ran, or this element was remounted, while the stylesheet was
    // loading — either way this mount no longer owns anything to render into.
    if (mounts.get(el) !== record || record.container !== container) {
      return { onParentNavigate };
    }

    record.root = createRoot(container);
    record.root.render(createElement(StrictMode, null, config.render(router)));

    return { onParentNavigate };
  }

  function unmount(el: HTMLElement): void {
    const record = mounts.get(el);
    // Already torn down, or this element was never mounted by this app — a
    // stale caller must not reach through and tear down whichever mount
    // happens to be live now.
    if (!record) return;

    const wasCurrent = currentEl === el;
    mounts.delete(el);
    if (wasCurrent) currentEl = null;

    try {
      record.root?.unmount();
    } finally {
      record.root = null;
      disposeRecord(record);
      if (record.container?.isConnected) {
        record.container.remove();
      }
      record.container = null;
      // Document-level state is shared, so only the mount that still owns it
      // may reset it. A late unmount from a superseded mount would otherwise
      // clear the flag and portal target out from under the live one.
      if (wasCurrent) {
        config.clearAuth?.();
        markFederationMount(false);
        setEmbedPortalContainer(null);
      }
    }
  }

  return { mount, unmount };
}
